import React, { useMemo } from "react";
import { useTranslation } from 'react-i18next';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Typography
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from "react-redux";

import { getTourPricingBreakdown } from '../../utils/checkout';
import { formatPrice } from '../../utils/price';

const ResumenAccordion = () => {

    const { t } = useTranslation(['checkout', 'components']);
    const products = useSelector(state => state.cart.checkout.tours || []);

    const productsTotal = useMemo(() => (
        products.map(product => {
            const breakdown = getTourPricingBreakdown(product);
            return {
                name: product.name,
                total: Number(breakdown?.totals?.total) || 0,
                currency: product?.currency?.symbol || product?.tourDetails?.currency?.symbol || product?.details?.currency?.symbol || '\u20AC',
            };
        })
    ), [products]);

    const totals = useMemo(() => (
        products.reduce((accumulator, product) => {
            const breakdown = getTourPricingBreakdown(product);
            const rawTaxRate = Number(breakdown?.totals?.iva_rate) || 0;
            return {
                subtotal: accumulator.subtotal + (Number(breakdown?.totals?.subtotal) || 0),
                iva: accumulator.iva + (Number(breakdown?.totals?.iva_total) || 0),
                total: accumulator.total + (Number(breakdown?.totals?.total) || 0),
                ivaApplies: accumulator.ivaApplies || rawTaxRate > 0,
            };
        }, { subtotal: 0, iva: 0, total: 0, ivaApplies: false })
    ), [products]);

    const cartCurrency = products?.[0]?.currency?.symbol || products?.[0]?.tourDetails?.currency?.symbol || products?.[0]?.details?.currency?.symbol || '\u20AC';

    return (
        <>
            <Accordion
                sx={{
                    '.Mui-expanded': {
                        minHeight: 'unset!important',
                    },
                    "::before": {
                        backgroundColor: 'unset'
                    },
                    '& .MuiDivider-root': {
                        display: 'none'
                    },
                    boxShadow: 'none',
                    mb: '0!important'
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-resumen-content`}
                    id={`panel-resumen-header`}
                    sx={{
                        '.Mui-expanded': {
                            my: '0!important',
                        },
                        '& .MuiAccordionSummary-content': {
                            my: 0
                        },
                        minHeight: 'unset',
                        color: 'secondary.main',
                        fontWeight: 'bold',
                        px: 0,
                        borderBottom: '1px solid #223F7F',
                    }}
                >
                    <Typography component="span">
                        {t('purchase-summary')}
                    </Typography>
                </AccordionSummary>
                {productsTotal && productsTotal.map(f => {
                    return (<AccordionDetails
                        key={f.name}
                        sx={{
                            p: '0'
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Typography sx=
                                {{
                                    color: 'secondary.main',
                                    fontWeight: 200,
                                    fontSize: '14px'
                                }}
                            >{f.name}</Typography>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        color: 'secondary.main'
                                    }}>
                                    {`${formatPrice(f.total)}${f.currency}`}
                                </Typography>
                            </Box>
                        </Box>
                    </AccordionDetails>)
                })}

            </Accordion>
            <Box sx={{ width: '100%', pt: '10px' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
                    <Typography sx={{ color: 'secondary.main', fontWeight: 200, fontSize: '14px' }}>Subtotal:</Typography>
                    <Typography sx={{ color: 'secondary.main', fontWeight: 400, fontSize: '14px' }}>
                        {`${formatPrice(totals.subtotal)}${cartCurrency}`}
                    </Typography>
                </Box>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
                    <Typography sx={{ color: 'secondary.main', fontWeight: 200, fontSize: '14px' }}>
                        {totals.ivaApplies ? 'IVA:' : 'IVA (No aplica):'}
                    </Typography>
                    <Typography sx={{ color: 'secondary.main', fontWeight: 400, fontSize: '14px' }}>
                        {`${formatPrice(totals.iva)}${cartCurrency}`}
                    </Typography>
                </Box>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: 'secondary.main', fontWeight: 700, fontSize: '14px' }}>Total:</Typography>
                    <Typography sx={{ color: 'secondary.main', fontWeight: 700, fontSize: '16px' }}>
                        {`${formatPrice(totals.total)}${cartCurrency}`}
                    </Typography>
                </Box>
            </Box>
        </>
    )
}

export default ResumenAccordion;
